import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Stack, TextField, Button, Alert,
  Switch, FormControlLabel,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton,
  Grid,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { botsApi } from "../../../api/bots.js";
import type { Bot, EmbedConfig } from "@chatfab/shared";

interface Props { bot: Bot; onUpdate: () => void }

export function SettingsTab({ bot, onUpdate }: Props) {
  const [name, setName]           = useState(bot.name);
  const [desc, setDesc]           = useState(bot.description ?? "");
  const [prompt, setPrompt]       = useState(bot.system_prompt);
  const [slug, setSlug]           = useState(bot.slug);
  const [slugEditing, setSlugEditing] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [temperature, setTemperature] = useState(bot.model_config.temperature);

  // Rate limits live in embed config but belong conceptually in general settings
  const [embedConfig, setEmbedConfig] = useState<EmbedConfig>(bot.embed_config);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saves: Promise<unknown>[] = [
        botsApi.update(bot.bot_id, {
          name,
          description: desc,
          system_prompt: prompt,
          model_config: { model_id: bot.model_config.model_id, max_tokens: bot.model_config.max_tokens, temperature },
        }),
      ];
      saves.push(botsApi.updateEmbed(bot.bot_id, embedConfig));
      await Promise.all(saves);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onUpdate();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveSlug = async () => {
    setError(null);
    try {
      const res = await botsApi.setSlug(bot.bot_id, slug);
      setSlug(res.slug);
      setSlugEditing(false);
      onUpdate();
    } catch {
      setError("Failed to update slug — it may already be taken or require a paid plan.");
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Settings</Typography>
        <Stack direction="row" spacing={1.5}>
          {saved && <Alert severity="success" sx={{ py: 0.5 }}>Saved!</Alert>}
          {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Button>
        </Stack>
      </Stack>

      <Stack spacing={3}>
        {/* Basic info */}
        <Card>
          <CardContent>
            <Typography variant="h5" mb={2}>Basic Info</Typography>
            <Stack spacing={2}>
              <TextField
                label="Bot name" fullWidth required
                value={name} onChange={e => setName(e.target.value)}
              />
              <TextField
                label="Description" fullWidth multiline rows={2}
                value={desc} onChange={e => setDesc(e.target.value)}
                placeholder="What does this bot help with?"
              />
              <TextField
                label="System prompt" fullWidth required multiline rows={6}
                value={prompt} onChange={e => setPrompt(e.target.value)}
                helperText="Sets the tone and behavior of your bot."
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Access */}
        <Card>
          <CardContent>
            <Typography variant="h5" mb={2}>Access</Typography>
            <Stack spacing={2}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Public URL: <strong>{window.location.origin}/b/{slug}</strong>
                  </Typography>
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/b/${slug}`)}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {slugEditing ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small" label="Custom slug"
                      value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                      sx={{ width: 220 }}
                      helperText="Lowercase letters, numbers, hyphens only"
                    />
                    <Button variant="contained" size="small" onClick={saveSlug}>Save Slug</Button>
                    <Button size="small" onClick={() => { setSlugEditing(false); setSlug(bot.slug); }}>Cancel</Button>
                  </Stack>
                ) : (
                  <Button size="small" variant="outlined" onClick={() => setSlugEditing(true)}>
                    Change slug
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                To require an invite code, go to the <strong>Invites</strong> tab.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Rate limits */}
        <Card>
            <CardContent>
              <Typography variant="h5" mb={0.5}>Rate Limits</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Applies to both the embed widget and the public bot URL.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Messages per session" type="number" fullWidth size="small"
                    value={embedConfig.per_session_cap}
                    onChange={e => setEmbedConfig(c => ({ ...c, per_session_cap: parseInt(e.target.value) || 0 }))}
                    helperText="0 = unlimited" />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Messages per IP / day" type="number" fullWidth size="small"
                    value={embedConfig.per_ip_daily_cap}
                    onChange={e => setEmbedConfig(c => ({ ...c, per_ip_daily_cap: parseInt(e.target.value) || 0 }))}
                    helperText="0 = unlimited" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Limit reached message" fullWidth size="small"
                    value={embedConfig.cta_on_limit ?? ""}
                    onChange={e => setEmbedConfig(c => ({ ...c, cta_on_limit: e.target.value }))}
                    placeholder="You've reached the limit. Need more help? Email us." />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

        {/* Danger zone */}
        <Card sx={{ borderColor: "error.light", borderWidth: 1, borderStyle: "solid" }}>
          <CardContent>
            <Typography variant="h5" color="error.main" mb={1}>Danger Zone</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Deleting this bot will remove all associated documents and data. This cannot be undone.
            </Typography>
            <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
              Delete this bot
            </Button>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete bot?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{bot.name}</strong>, all documents, and all embed settings.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={async () => { await botsApi.delete(bot.bot_id); }}
          >
            Delete bot
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
